import React, { useEffect, useState, useCallback } from "react";
import { getApiUrl } from "../config/api";
import { useWebSocket } from "../context/WebSocketContext";
import { formatDateTime } from "../utils/dateUtils";

export default function ChatHistory({ clientId, clientName, activeRfq }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const { subscribe, sendMessage: wsSendMessage, connectionStatus } = useWebSocket();

  // When activeRfq changes, try to select that conversation
  useEffect(() => {
    if (activeRfq?.rfqNumber && conversations.length > 0) {
      const conv = conversations.find(c => c.rfqNumber === activeRfq.rfqNumber);
      if (conv && conv.rfqNumber !== selectedConv?.rfqNumber) {
        selectConversation(conv);
      }
    }
  }, [activeRfq, conversations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!clientId) return;
      setLoading(true);
      try {
        // Add userType parameter as required by backend
        const res = await fetch(getApiUrl(`/api/chat/user/${encodeURIComponent(clientId)}?userType=client`));
        if (!res.ok) throw new Error('Failed to fetch user chats');
        const data = await res.json();
        const convs = data?.conversations || [];
        if (mounted) setConversations(convs);
      } catch (err) {
        console.warn('[ChatHistory] failed to load conversations', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [clientId]);

  // Subscribe to WebSocket messages for selected conversation
  useEffect(() => {
    if (!selectedConv?.rfqNumber) return;
    
    const handleMessage = (msg) => {
      setMessages((prev) => [...prev, msg]);
    };
    
    // Subscribe to shared WebSocket connection
    const unsubscribe = subscribe(selectedConv.rfqNumber, handleMessage);
    
    return unsubscribe;
  }, [selectedConv, subscribe]);

  const selectConversation = async (conv) => {
    setSelectedConv(conv);
    
    // Load existing messages from the API
    try {
      const res = await fetch(getApiUrl(`/api/chat/${conv.rfqNumber}/messages?userId=${clientId}&userType=client`));
      if (!res.ok) {
        setMessages(conv.messages || []);
        return;
      }
      const data = await res.json();
      setMessages(data.messages || conv.messages || []);
    } catch (error) {
      console.error(`[ChatHistory] Error loading messages:`, error);
      setMessages(conv.messages || []);
    }
  };

  const sendMessage = useCallback(() => {
    if (!selectedConv?.rfqNumber) return;
    
    const status = connectionStatus[selectedConv.rfqNumber];
    if (status !== 'open') {
      alert('Chat connection not open');
      return;
    }
    
    const payload = {
      senderId: clientId,
      senderType: 'client',
      senderName: clientName || 'Client',
      content: draft,
    };
    
    const success = wsSendMessage(selectedConv.rfqNumber, payload);
    if (success) {
      setDraft('');
    }
  }, [selectedConv, clientId, clientName, draft, connectionStatus, wsSendMessage]);

  const filteredConversations = conversations.filter(c => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      (c.rfqNumber && c.rfqNumber.toLowerCase().includes(searchLower)) ||
      (c.otherPartyName && c.otherPartyName.toLowerCase().includes(searchLower)) ||
      (c.material && c.material.toLowerCase().includes(searchLower)) ||
      (c.subtype && c.subtype.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="flex h-full bg-white dark:bg-gray-900 overflow-hidden">
      <div className="w-72 border-r border-gray-200 dark:border-gray-700 flex flex-col flex-shrink-0 overflow-hidden">
        <div className="p-3 border-b dark:border-gray-700 bg-white dark:bg-gray-900 flex-shrink-0">
          <div className="font-medium mb-2 text-gray-900 dark:text-gray-100">Conversations</div>
          <input
            type="text"
            placeholder="Search by RFQ #, dealer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-3 text-sm text-gray-500 dark:text-gray-400">Loading...</div>}
          {!loading && filteredConversations.length === 0 && (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-sm font-medium">No conversations yet</p>
              <p className="text-xs mt-1">Start a chat from an RFQ to begin messaging</p>
            </div>
          )}
          {filteredConversations.map((c) => (
            <button key={c.rfqNumber} onClick={() => selectConversation(c)} className={`w-full text-left p-3 border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 ${selectedConv?.rfqNumber===c.rfqNumber ? 'bg-gray-100 dark:bg-gray-800' : ''}`}>
              {console.log(c)}
              <div className="font-medium text-gray-900 dark:text-gray-100">{ c.otherPartyName + " at " + c.otherPartyCompany || 'Conversation'}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">RFQ: {c.rfqNumber}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 flex flex-col bg-white dark:bg-gray-900 overflow-hidden">
        {!selectedConv ? (
          <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
            <div className="text-center text-gray-500 dark:text-gray-400 p-8">
              <svg className="mx-auto h-20 w-20 text-gray-300 dark:text-gray-600 mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
              </svg>
              <p className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Welcome to Chat</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                Select a conversation from the list to view your message history and continue the discussion.
              </p>
              {/* Tip removed per UX request */}
            </div>
          </div>
        ) : (
          <>
            <div className="p-3 border-b dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-900 flex-shrink-0">
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100">{selectedConv?.otherPartyName + " at " + selectedConv?.otherPartyCompany || 'Select a conversation'}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">RFQ: {selectedConv?.rfqNumber}</div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-800">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    <p className="text-sm">No messages yet</p>
                    <p className="text-xs mt-1">Start the conversation below</p>
                  </div>
                </div>
              ) : (
                messages.map((m, i) => (
                  <div key={i} className={`mb-3 ${m.senderType==='client' ? 'text-right' : 'text-left'}`}>
                    <div className={`inline-block px-3 py-2 rounded ${m.senderType==='client' ? 'bg-blue-600 dark:bg-blue-700 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100'}`}>
                      <div className="text-sm">{m.content || m.text || m.message}</div>
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{m.senderName || (m.senderType==='client' ? 'You' : '')} • {m.timestamp ? formatDateTime(m.timestamp) : ''}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="p-3 border-t dark:border-gray-700 flex gap-2 bg-white dark:bg-gray-900 flex-shrink-0">
              <input 
                value={draft} 
                onChange={(e) => setDraft(e.target.value)} 
                onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
                placeholder="Type a message..." 
                className="flex-1 px-3 py-2 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" 
              />
              <button onClick={sendMessage} className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-800">Send</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
